"use client";

import type {
	WhopElements,
	WhopElementsOptions,
} from "@whop/embedded-components-vanilla-js/types";

import React, { useSyncExternalStore } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";

interface ContextState {
	elements: WhopElements | null;
}

const ElementsContext = createContext<ContextState | null>(null);

export function useElements() {
	const ctx = useContext(ElementsContext);
	if (!ctx) {
		throw new Error("useElements must be used within a ElementsProvider");
	}

	return ctx.elements;
}

type ElementsProp = PromiseLike<WhopElements | null> | WhopElements | null;

function normalizeElementsProp(
	prop: ElementsProp,
): Promise<Awaited<ElementsProp>> {
	if (prop instanceof Promise) return prop;
	return Promise.resolve(prop);
}

class PromiseStore<T> {
	private subscribers: Set<() => void> = new Set();
	private value:
		| {
				state: "pending";
		  }
		| {
				state: "fulfilled";
				value: T;
		  }
		| {
				state: "rejected";
				error: unknown;
		  } = { state: "pending" };

	constructor(promise: Promise<T>) {
		promise
			.then((value) => {
				this.value = { state: "fulfilled", value };
				for (const subscriber of this.subscribers) {
					subscriber();
				}
			})
			.catch((err) => {
				this.value = { state: "rejected", error: err };
				for (const subscriber of this.subscribers) {
					subscriber();
				}
			});
	}

	public getSnapshot() {
		return this.value;
	}

	public subscribe = (listener: () => void) => {
		this.subscribers.add(listener);
		return () => {
			this.subscribers.delete(listener);
		};
	};
}

function useAwaitElements(elements: ElementsProp | null) {
	const store = useMemo(
		() => new PromiseStore(Promise.resolve(elements)),
		[elements],
	);

	const state = useSyncExternalStore(
		store.subscribe,
		() => {
			const snapshot = store.getSnapshot();
			if (snapshot.state === "fulfilled") {
				return snapshot.value;
			}
			return null;
		},
		() => null,
	);

	return state;
}

export function Elements({
	children,
	elements: elementsProp,
	appearance,
	locale,
}: {
	children: React.ReactNode;
	elements: ElementsProp;
} & WhopElementsOptions) {
	const elementsPromise = normalizeElementsProp(elementsProp);
	const elements = useAwaitElements(elementsPromise);

	useEffect(() => {
		if (!elements) return;
		elements.updateOptions({ appearance, locale });
	}, [elements, appearance, locale]);

	const value: ContextState = useMemo(
		() => ({
			elements,
		}),
		[elements],
	);

	return (
		<ElementsContext.Provider value={value}>
			{children}
		</ElementsContext.Provider>
	);
}
